// SPDX-License-Identifier: Elastic-2.0
/**
 * pushPermission — thin testable wrapper over the global `Notification`
 * API used by the Notifications section of Settings (F-320 / T-003).
 *
 * Per the technical spec § 3, the wrapper exists so that the section
 * component (T-004) imports two pure functions that are trivial to mock
 * with `vi.mock`, and so that browsers / webviews without the
 * `Notification` global don't crash the settings page.
 *
 * Behaviour:
 *   - `getPushPermission()` returns the live browser permission, or
 *     `"unsupported"` when the global is absent.
 *   - `requestPushPermission()` awaits `Notification.requestPermission()`
 *     and returns its resolved value. Older Safari fires the legacy
 *     callback API instead of returning a Promise — we catch the throw
 *     and report `"default"` so the user can retry.
 *
 * Spec: `specs/wip/feat-49-notifications-section/technical/notifications-section.md` § 3
 */

export type PushPermissionState =
  | "granted"
  | "denied"
  | "default"
  | "unsupported";

export function getPushPermission(): PushPermissionState {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission;
}

export async function requestPushPermission(): Promise<PushPermissionState> {
  if (typeof Notification === "undefined") return "unsupported";
  try {
    const result = await Notification.requestPermission();
    return result;
  } catch {
    // Older Safari fires the legacy callback API; modern browsers all
    // support the Promise variant. Treat throws as "default" so the
    // caller can retry.
    return "default";
  }
}
